/**
 * 業務「選定対象を引き当てる」（1955）のロジック図の見本 2 案（mock）。実データは読まない。
 *
 * どちらも表現の方法を画面で比べるための見本で、URL を直に打ったときだけ見える（Dhik 2026-10-01
 * 「他はデータは mock で UI としてわかるようにして」「ボタンは作らなくていいよ」）。
 *
 * - **v2**: マッチ度を業務ルールで表す案。箱は業務ルールの番号を持ち、押すと `mock-1955-rules.ts` の中身を開く。Figma の図の節点を 1 対 1 で写す（21 個。表は図に入れない） —
 *   属性ごとの適合を判定する業務ルール → 適合度 → マッチ度を算出する業務ルール → マッチ度 → 並べる
 * - **v3**: 業務ルールを使わず、判断をすべて分岐でつないだ木。判定表の列を 1 つずつ分岐に開き、
 *   並べ替えも「そのキーで差がつくか」の分岐の連なりで表す
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1955 のロジック図の見本として、マッチ度を業務ルールで表す案（v2）と、判断をすべて分岐で表す案（v3）の材料を持つ。
 * @graph-connects none
 */
import type { LogicDiagramModel } from './logic-model.js';

/** 見本を持つ業務の番号。 @graph-connects none */
export const LOGIC_MOCK_BUSINESS_ID = 1955;

/** 終了①（選定途中のユーザーを返す）の中の行。 @graph-connects none */
const END_IN_PROGRESS = ['遷移先業務：選定を開始する', '返り値：選定途中のユーザー情報'];
/** 終了②（対象なし）の中の行。 @graph-connects none */
const END_NONE = [
  '遷移先業務：マイページを見る',
  '返り値：エラー「選定可能なユーザーはいませんでした」',
];
/** 終了③（引き当てた）の中の行。 @graph-connects none */
const END_ASSIGNED = ['遷移先業務：選定を開始する', '返り値：選定対象のユーザー情報'];

/** 選定可能かを決める会員の属性。 @graph-connects none */
const MEMBER_INPUTS = [
  '会員の会員ステータス',
  '会員のレンタルステータス',
  '会員の選定保留フラグ',
  '会員のサービス停止ステータス',
  '選定対象の選定希望日の有無',
];

/** マッチ度の元になる属性。 @graph-connects none */
const MATCH_INPUTS = [
  'スタイリストのマタニティ経験',
  'スタイリストの初回優先フラグ',
  '会員のマタニティ予定',
  '会員のオーダー注文回数',
];

/** 並べ替えに使うオーダーの属性。 @graph-connects none */
const ORDER_INPUTS = [
  'オーダーの選定要求日',
  'オーダーの指名情報',
  'オーダーの二重配送',
  'オーダーの配送優先度',
];

/**
 * v2: マッチ度を業務ルールで表す案（Figma の図）。
 * @graph-connects none
 */
export const LOGIC_MOCK_V2: LogicDiagramModel = {
  nodes: [
    { key: 'in-progress', kind: 'input', title: '', lines: ['選定途中のユーザーの情報'], lane: 0 },
    {
      key: 'branch-progress',
      kind: 'branch',
      title: '選定途中のユーザーがいるか',
      lines: [],
      lane: 0,
      ruleId: 2346,
    },
    { key: 'end-progress', kind: 'end', title: '終了①', lines: END_IN_PROGRESS, lane: 0 },
    { key: 'in-member', kind: 'input', title: '', lines: MEMBER_INPUTS, lane: 1 },
    {
      key: 'rule-judge',
      kind: 'rule',
      title: '選定可能なユーザーの判定をする',
      lines: [],
      lane: 1,
      ruleId: 1898,
    },
    {
      key: 'out-candidates',
      kind: 'ruleOutput',
      title: '選定可能なユーザーリスト',
      lines: [],
      lane: 1,
    },
    {
      key: 'branch-count',
      kind: 'branch',
      title: '選定可能なユーザーリストの数',
      lines: [],
      lane: 1,
      ruleId: 1904,
    },
    { key: 'end-none', kind: 'end', title: '終了②', lines: END_NONE, lane: 0 },
    { key: 'in-match', kind: 'input', title: '', lines: MATCH_INPUTS, lane: 3 },
    {
      key: 'rule-fit',
      kind: 'rule',
      title: 'スタイリスト属性と会員属性から適合を判定する',
      lines: [],
      lane: 3,
      ruleId: 9101,
    },
    { key: 'out-fit', kind: 'ruleOutput', title: '適合度', lines: [], lane: 3 },
    { key: 'in-weight', kind: 'input', title: '', lines: ['属性の適合度の重み'], lane: 4 },
    {
      key: 'rule-match',
      kind: 'rule',
      title: 'スタイリスト属性と会員属性からマッチ度を算出する',
      lines: [],
      lane: 3,
      ruleId: 9102,
    },
    { key: 'out-match', kind: 'ruleOutput', title: 'マッチ度', lines: [], lane: 3 },
    { key: 'in-order', kind: 'input', title: '', lines: ORDER_INPUTS, lane: 2 },
    {
      key: 'rule-sort',
      kind: 'rule',
      title: '選定可能なユーザーを並べる',
      lines: [],
      lane: 2,
      ruleId: 1903,
    },
    {
      key: 'out-sorted',
      kind: 'ruleOutput',
      title: '選定可能なユーザーリスト',
      lines: [],
      lane: 2,
    },
    {
      key: 'rule-top',
      kind: 'rule',
      title: '上位 1 件を取得する',
      lines: [],
      lane: 2,
      ruleId: 1903,
    },
    { key: 'out-top', kind: 'ruleOutput', title: '選定可能なユーザーリスト', lines: [], lane: 2 },
    {
      key: 'effect-start',
      kind: 'effect',
      title: '選定対象のユーザーを選定中に変更する',
      lines: [],
      lane: 2,
      ruleId: 2347,
    },
    { key: 'end-assigned', kind: 'end', title: '終了③', lines: END_ASSIGNED, lane: 2 },
  ],
  edges: [
    { from: 'in-progress', to: 'branch-progress' },
    { from: 'branch-progress', to: 'end-progress', label: 'いる' },
    { from: 'branch-progress', to: 'rule-judge', label: 'いない' },
    { from: 'in-member', to: 'rule-judge' },
    { from: 'rule-judge', to: 'out-candidates' },
    { from: 'out-candidates', to: 'branch-count' },
    { from: 'branch-count', to: 'end-none', label: '0' },
    { from: 'branch-count', to: 'rule-sort', label: '1 以上' },
    { from: 'in-match', to: 'rule-fit' },
    { from: 'rule-fit', to: 'out-fit' },
    { from: 'out-fit', to: 'rule-match' },
    { from: 'in-weight', to: 'rule-match' },
    { from: 'rule-match', to: 'out-match' },
    { from: 'out-match', to: 'rule-sort' },
    { from: 'in-order', to: 'rule-sort' },
    { from: 'rule-sort', to: 'out-sorted' },
    { from: 'out-sorted', to: 'rule-top' },
    { from: 'rule-top', to: 'out-top' },
    { from: 'out-top', to: 'effect-start' },
    { from: 'effect-start', to: 'end-assigned' },
  ],
};

/** 対象外（リストに入れない）の節点を段 1 に並べる。 @graph-connects none */
function excluded(key: string): LogicDiagramModel['nodes'][number] {
  return { key, kind: 'ruleOutput', title: '対象外（リストに入れない）', lines: [], lane: 1 };
}

/**
 * v3: 判断をすべて分岐でつないだ木。業務ルールは持たない。
 * @graph-connects none
 */
export const LOGIC_MOCK_V3: LogicDiagramModel = {
  nodes: [
    { key: 'in-progress', kind: 'input', title: '', lines: ['選定途中のユーザーの情報'], lane: 0 },
    { key: 'b-progress', kind: 'branch', title: '選定途中のユーザーがいるか', lines: [], lane: 0 },
    { key: 'end-progress', kind: 'end', title: '終了①', lines: END_IN_PROGRESS, lane: 1 },
    { key: 'in-member', kind: 'input', title: '', lines: MEMBER_INPUTS, lane: 2 },
    { key: 'b-status', kind: 'branch', title: '会員の会員ステータスは', lines: [], lane: 0 },
    excluded('x-status'),
    { key: 'b-rental', kind: 'branch', title: '会員のレンタルステータスは', lines: [], lane: 0 },
    excluded('x-rental'),
    { key: 'b-hold', kind: 'branch', title: '会員の選定保留フラグは', lines: [], lane: 0 },
    excluded('x-hold'),
    { key: 'b-stop', kind: 'branch', title: '会員のサービス停止ステータスは', lines: [], lane: 0 },
    excluded('x-stop'),
    { key: 'b-date', kind: 'branch', title: '選定対象の選定希望日は', lines: [], lane: 0 },
    excluded('x-date'),
    {
      key: 'out-candidates',
      kind: 'ruleOutput',
      title: '選定可能なユーザーリスト',
      lines: [],
      lane: 0,
    },
    { key: 'b-count', kind: 'branch', title: '選定可能なユーザーリストの数', lines: [], lane: 0 },
    { key: 'end-none', kind: 'end', title: '終了②', lines: END_NONE, lane: 1 },
    {
      key: 'in-match',
      kind: 'input',
      title: '',
      lines: [...MATCH_INPUTS, '属性の適合度の重み'],
      lane: 2,
    },
    {
      key: 'b-maternity',
      kind: 'branch',
      title: 'マタニティ経験とマタニティ予定がそろうか',
      lines: ['そろう：両方あり ／ 両方なし', 'そろう → ＋重み A ／ そろわない → ＋0'],
      lane: 0,
    },
    {
      key: 'b-first',
      kind: 'branch',
      title: '初回優先フラグと初回のオーダーがそろうか',
      lines: ['そろう：両方あり ／ 両方なし', 'そろう → ＋重み B ／ そろわない → ＋0'],
      lane: 0,
    },
    { key: 'out-match', kind: 'ruleOutput', title: 'マッチ度', lines: [], lane: 0 },
    { key: 'in-order', kind: 'input', title: '', lines: ORDER_INPUTS, lane: 2 },
    {
      key: 'b-key1',
      kind: 'branch',
      title: '選定要求日の状況で差がつくか',
      lines: ['期限超過 → 当日 → 未来'],
      lane: 0,
    },
    {
      key: 'b-key2',
      kind: 'branch',
      title: '指名区分で差がつくか',
      lines: ['個人 → グループ → なし'],
      lane: 0,
    },
    {
      key: 'b-key3',
      kind: 'branch',
      title: '二重配送で差がつくか',
      lines: ['該当する → 該当しない'],
      lane: 0,
    },
    {
      key: 'b-key4',
      kind: 'branch',
      title: '配送優先度で差がつくか',
      lines: ['大きい順'],
      lane: 0,
    },
    { key: 'b-key5', kind: 'branch', title: 'マッチ度で差がつくか', lines: ['高い順'], lane: 0 },
    { key: 'b-key6', kind: 'branch', title: '選定希望日で差がつくか', lines: ['早い順'], lane: 0 },
    { key: 'out-top', kind: 'ruleOutput', title: '先頭の 1 件', lines: [], lane: 1 },
    {
      key: 'effect-start',
      kind: 'effect',
      title: '選定対象のユーザーを選定中に変更する',
      lines: [],
      lane: 1,
    },
    { key: 'end-assigned', kind: 'end', title: '終了③', lines: END_ASSIGNED, lane: 1 },
  ],
  edges: [
    { from: 'in-progress', to: 'b-progress' },
    { from: 'b-progress', to: 'end-progress', label: 'いる' },
    { from: 'b-progress', to: 'b-status', label: 'いない（会員ごとに）' },
    { from: 'in-member', to: 'b-status' },
    { from: 'b-status', to: 'x-status', label: '無料会員' },
    { from: 'b-status', to: 'b-rental', label: '月額会員' },
    { from: 'b-rental', to: 'x-rental', label: '選定待ち以外' },
    { from: 'b-rental', to: 'b-hold', label: '選定待ち' },
    { from: 'b-hold', to: 'x-hold', label: '選定保留中' },
    { from: 'b-hold', to: 'b-stop', label: '選定保留中ではない' },
    { from: 'b-stop', to: 'x-stop', label: 'サービス停止中' },
    { from: 'b-stop', to: 'b-date', label: 'サービス停止以外' },
    { from: 'b-date', to: 'x-date', label: '未来の日付' },
    { from: 'b-date', to: 'out-candidates', label: '現在時刻以前' },
    { from: 'out-candidates', to: 'b-count' },
    { from: 'b-count', to: 'end-none', label: '0' },
    { from: 'b-count', to: 'b-maternity', label: '1 以上（候補ごとに）' },
    { from: 'in-match', to: 'b-maternity' },
    { from: 'b-maternity', to: 'b-first', label: '加点して次へ' },
    { from: 'b-first', to: 'out-match', label: '加点して合計' },
    { from: 'out-match', to: 'b-key1' },
    { from: 'in-order', to: 'b-key1' },
    { from: 'b-key1', to: 'out-top', label: '差がつく：上を採る' },
    { from: 'b-key1', to: 'b-key2', label: '同じ' },
    { from: 'b-key2', to: 'out-top', label: '差がつく' },
    { from: 'b-key2', to: 'b-key3', label: '同じ' },
    { from: 'b-key3', to: 'out-top', label: '差がつく' },
    { from: 'b-key3', to: 'b-key4', label: '同じ' },
    { from: 'b-key4', to: 'out-top', label: '差がつく' },
    { from: 'b-key4', to: 'b-key5', label: '同じ' },
    { from: 'b-key5', to: 'out-top', label: '差がつく' },
    { from: 'b-key5', to: 'b-key6', label: '同じ' },
    { from: 'b-key6', to: 'out-top', label: '差がつく ／ 同じ（先に来た方）' },
    { from: 'out-top', to: 'effect-start' },
    { from: 'effect-start', to: 'end-assigned' },
  ],
};
